import React, { useState } from "react";
import { Box, Button, Link, Stack, Typography } from "@mui/material";
import { Link as RouterLink, redirect, useNavigate } from "react-router-dom";
import { FirebaseError } from "firebase/app";
import { useDispatch } from "react-redux";
import * as yup from "yup";
import { useFormik } from "formik";
import AuthTextField from "components/AuthTextField";
import { signIn } from "services/user";
import Switch from "components/Switch";
import AuthLayout from "layouts/AuthLayout";
import { setAuthentication, setUser } from "store/userSlice";

// https://formik.org/docs/examples/with-material-ui
// https://dev.to/vikirobles/react-authentication-with-firebase-v9-typescript-and-yup-4025

const loginValidationSchema = yup.object({
  email: yup
    .string()
    .email("Enter a valid email")
    .required("Email is required"),
  password: yup.string().required("Password is required"),
});

const Login = () => {
  const [firebaseEmailError, setFirebaseEmailError] = useState("");
  const [firebasePasswordError, setFirebasePasswordError] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: loginValidationSchema,
    onSubmit: ({ email, password }) => {
      signIn(email, password)
        .then((user) => {
          dispatch(setAuthentication(true));
          dispatch(setUser(user));
          navigate("/");
        })
        .catch((error: FirebaseError) => {
          setFirebaseEmailError("");
          setFirebasePasswordError("");
          switch (error.code) {
            case "auth/invalid-email":
              setFirebaseEmailError("Invalid email address");
              break;
            case "auth/user-disabled":
              setFirebaseEmailError("User disabled");
              break;
            case "auth/user-not-found":
              setFirebaseEmailError("Email not found");
              break;
            case "auth/wrong-password":
              setFirebasePasswordError("Incorrect password");
              break;
          }
        });
    },
  });

  return (
    <AuthLayout>
      <Stack component={"form"} onSubmit={formik.handleSubmit} spacing={3}>
        <Box>
          <Typography color={"primary.dark"} fontSize={32} fontWeight={700}>
            Welcome Back
          </Typography>
          <Typography color={"text.light"} pb={2}>
            Enter your email and password to sign in
          </Typography>
        </Box>

        <AuthTextField
          fullWidth
          label="Email"
          type="text"
          placeholder="Your email address"
          id="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          error={
            formik.touched.email &&
            (Boolean(formik.errors.email) || Boolean(firebaseEmailError))
          }
          helperText={
            formik.touched.email && (formik.errors.email || firebaseEmailError)
          }
        />
        <AuthTextField
          fullWidth
          label="Password"
          type="password"
          placeholder="Your password"
          id="password"
          value={formik.values.password}
          onChange={formik.handleChange}
          error={
            formik.touched.password &&
            (Boolean(formik.errors.password) || Boolean(firebasePasswordError))
          }
          helperText={
            formik.touched.password &&
            (formik.errors.password || firebasePasswordError)
          }
        />
        <Stack direction={"row"} alignItems={"center"}>
          <Switch /> <Typography paddingLeft={1.5}>Remember me</Typography>
        </Stack>
        <Button
          variant="contained"
          type="submit"
          disableElevation
          sx={{
            borderRadius: 3,
            height: 45,
            "&.MuiButton-root": {
              ":hover": {
                backgroundColor: "primary.light",
              },
            },
          }}
        >
          <Typography fontWeight={700} fontSize={12}>
            SIGN IN
          </Typography>
        </Button>
        <Typography textAlign={"center"} color={"text.light"}>
          Don't have an account?{" "}
          <Link
            component={RouterLink}
            to="/signup"
            color={"primary.blue"}
            fontWeight={700}
            underline="hover"
          >
            Sign up
          </Link>
        </Typography>
      </Stack>
    </AuthLayout>
  );
};

export default Login;
